import axios from "axios";
import LoginUtil from "@/login/LoginUtil";
import Links from "@/shared/constants/Links";
import urlUtil from "@/utils/urlUtil";
import toast from "@/shared/toast";

/**
 * Session-controlled auth handling. Owns the single-flight token refresh,
 * the idempotent "session lost" handler, and a response interceptor that
 * can be installed on any axios instance.
 *
 * A token can now be revoked server-side before it expires, so any
 * authenticated request can return 401 with a valid-looking JWT.
 */

// Bare client with NO interceptors — used only for the refresh call so it
// can never recurse back through the 401 handler below.
const refreshClient = axios.create({ baseURL: urlUtil.getBaseUrl() });

// In-flight refresh promise; shared so concurrent 401s trigger one refresh.
let refreshPromise = null;

// Guards handleSessionLost so concurrent failures produce one toast + redirect.
let sessionLostHandled = false;

/**
 * Refresh the access token. Single-flight: concurrent callers share one
 * request. Resolves with the new access token, rejects if there is no
 * refresh token or the refresh call fails.
 * @returns {Promise<string>}
 */
export function refreshSession() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshToken = await LoginUtil.getRefreshTokenAsync();
        if (!refreshToken) {
            throw new Error("No refresh token");
        }
        const res = await refreshClient.put(Links.auth.refreshToken, {
            token: refreshToken,
        });
        // The refresh token is rotated on every refresh — store both.
        await LoginUtil.setAccessToken(res.data.accessToken);
        await LoginUtil.setRefreshToken(res.data.refreshToken);
        return res.data.accessToken;
    })().finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
}

/**
 * The session is gone. Clear tokens, tell the user, and send them to login.
 * Idempotent — safe to call from many concurrent failed requests.
 */
export function handleSessionLost() {
    if (sessionLostHandled) return;
    sessionLostHandled = true;

    LoginUtil.resetAccessToken();
    LoginUtil.resetRefreshToken();

    toast.open({
        message: "Your session has ended. Please sign in again.",
        type: "warning",
        position: "top-right",
    });

    setTimeout(() => {
        window.location.href = "/login";
    }, 1500);
}

/**
 * Install the session-aware response interceptor on an axios instance.
 * On 401 it attempts one refresh and retries the original request;
 * if the refresh fails it triggers handleSessionLost().
 * @param {import("axios").AxiosInstance} instance
 */
export function installResponseInterceptor(instance) {
    instance.interceptors.response.use(
        (response) => {
            // Preserve existing behaviour: empty body -> null.
            if (response.data === "") {
                response.data = null;
            }
            return response;
        },
        async (err) => {
            const originalConfig = err.config;

            if (!err.response || err.response.status !== 401 || !originalConfig) {
                return Promise.reject(err);
            }

            const url = originalConfig.url || "";
            // Never refresh for the login or refresh-token calls themselves.
            if (url.includes("authenticate") || url.includes("refresh-token")) {
                return Promise.reject(err);
            }

            // A request that already retried and still 401s -> session truly gone.
            if (originalConfig._retry) {
                handleSessionLost();
                return Promise.reject(err);
            }
            originalConfig._retry = true;

            try {
                const newToken = await refreshSession();
                originalConfig.headers = originalConfig.headers || {};
                originalConfig.headers.Authorization = `Bearer ${newToken}`;
                return instance(originalConfig);
            } catch (refreshErr) {
                handleSessionLost();
                return Promise.reject(err);
            }
        }
    );
}
